/**
 * Semantic colors as CSS variable references, for styled-components and
 * inline styles. They resolve to the tokens in shadcn/styles/global.css, so
 * they follow the active theme (`.dark`), unlike the literal hex values in
 * `colors.ts`.
 *
 * These are `var(...)` strings, not hex values: don't pass them to color math
 * (tinycolor, polished, hex + alpha concatenation) or to APIs that need a
 * literal color, such as canvas or Monaco themes.
 *
 * Light-theme equivalents (keep in sync with global.css):
 * - foreground              GREY_900
 * - subtleForeground        GREY_700
 * - mutedForeground         GREY_500
 * - faintForeground         GREY_300
 * - background/card/popover WHITE
 * - tint                    GREY_25 (provisional)
 * - muted / accent          GREY_50
 * - accentStrong            GREY_100 as a fill (provisional)
 * - border / input          GREY_100 as a border
 * - borderStrong            GREY_200 (provisional)
 * - ring                    GREY_300
 * - hairline                GREY_100_O (provisional)
 * - inverse                 GREY_900 fill, e.g. tooltips (provisional)
 * - inverseForeground       WHITE on inverse (provisional)
 * - hairlineStrong          INK_20 focus ring around the prompt box (provisional)
 * - logoMark                GREY_900 tile behind the logo (provisional)
 */
export const semanticColors = {
  background: "var(--background)",
  foreground: "var(--foreground)",
  card: "var(--card)",
  cardForeground: "var(--card-foreground)",
  popover: "var(--popover)",
  popoverForeground: "var(--popover-foreground)",
  primary: "var(--primary)",
  primaryForeground: "var(--primary-foreground)",
  primaryHover: "var(--primary-hover)",
  secondary: "var(--secondary)",
  secondaryForeground: "var(--secondary-foreground)",
  muted: "var(--muted)",
  mutedForeground: "var(--muted-foreground)",
  accent: "var(--accent)",
  accentForeground: "var(--accent-foreground)",
  destructive: "var(--destructive)",
  subtleForeground: "var(--subtle-foreground)",
  faintForeground: "var(--faint-foreground)",
  border: "var(--border)",
  input: "var(--input)",
  ring: "var(--ring)",

  // Provisional (see global.css)
  tint: "var(--tint)",
  accentStrong: "var(--accent-strong)",
  borderStrong: "var(--border-strong)",
  hairline: "var(--hairline)",
  hairlineStrong: "var(--hairline-strong)",
  inverse: "var(--inverse)",
  inverseForeground: "var(--inverse-foreground)",
  logoMark: "var(--logo-mark)",
} as const;

/** The primary blue at `percent` opacity, for selected rows, tints and focus halos. */
export const primaryAlpha = (percent: number): string =>
  `color-mix(in srgb, var(--primary) ${percent}%, transparent)`;
